import {
  EnvironmentProviders,
  inject,
  provideAppInitializer,
} from '@angular/core';
import { BdFeaturePermissionService } from '@bindoc-projects/bindoc-permissions-lib/data';
import { BdAuthenticationService } from '@bindoc-projects/security-lib/data';
import { filter, switchMap, take } from 'rxjs/operators';

/**
 * Prefetches the caller's feature permissions the moment MSAL signals an
 * authenticated user, so the menu and permission-gated UI no longer have to wait
 * for a route to activate before the HTTP call starts. `shareReplay(1)` inside
 * the service dedupes this against the later menu subscription.
 *
 * Gated on `msalInitialized` rather than reading `authentication$` directly:
 * that subject starts `false`, so an unguarded read would fire the call for an
 * anonymous user (or miss the authenticated one).
 *
 * Fire-and-forget by design — the result is only ever consumed through the
 * service's cache, so a failure costs a cache miss, not a broken bootstrap.
 */
export function providePermissionPrefetch(): EnvironmentProviders {
  return provideAppInitializer(() => {
    const authService = inject(BdAuthenticationService);
    const permissionService = inject(BdFeaturePermissionService);
    authService.msalInitialized
      .pipe(
        switchMap(() => authService.authentication$),
        filter((authenticated) => authenticated),
        take(1),
        switchMap(() => permissionService.getPermissions()),
      )
      .subscribe();
  });
}
